import { buildFieldAnchors } from '../shared/previewFieldAnchors';

/**
 * Type and layout metrics lifted off the official ভোটার নিবন্ধন ফরম (ফরম-২,
 * বিধি-১০) — the two-sided sheet a new voter fills in at the registration
 * centre, with the নিবন্ধন স্লিপ (ফরম-৫) on the foot of page 1.
 *
 * The government file is a 300 dpi *scan* carrying an invisible OCR layer, not
 * vector art: the printed rules and comb cells are pixels, and the OCR text
 * decodes to garbage. So every number here was measured off a 4× render —
 * label ink extents and dot runs from per-row dark runs, the comb bands from
 * paired horizontal rules with their cell rules between, each reading checked
 * against a ruled crop of the scan.
 *
 * The preview re-typesets the form at these coordinates rather than stamping
 * the scan, so a printed page lines up with the real form.
 */

/** Page box — A4 at 72 dpi, same as the original. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/**
 * Font sizes (px = pt) and weights.
 *
 * Solved from the scan, not guessed: each label's measured ink width divided by
 * Kalpurush's advance for the same words, then checked by overlaying a render
 * of the preview on a render of the scan and measuring the ink of each label
 * in both. That overlay is what settled the body at 11.6 px: eyeballed crops
 * had put it at 10.8, which came out ~7% narrow across the sheet. This form is
 * set noticeably larger than Form-2's correction cousin — it is a roomier,
 * one-item-per-line document.
 */
export const TYPE = {
    title: { size: 11.5, weight: 400 },
    body: { size: 11.6, weight: 400 },
    /** The ২১ / ২৩ option strips and the slip's rule reference set smaller. */
    small: { size: 9.6, weight: 400 },
    /**
     * Item ৩৩'s sworn declaration runs as a paragraph and is the one place
     * the sheet sets genuinely small — Kalpurush needs 8.2 px to fit the same
     * sentence into the same 477 pt column.
     */
    note: { size: 8.2, weight: 400 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/**
 * Kalpurush's ascent is 1 em and its descent 0.4 em, so in that line box the
 * baseline lands 1.05 em below the top — the number that turns a measured
 * baseline into a CSS `top`.
 */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/**
 * The leaders are real full-stops in the form's own font — one every 3.05 pt,
 * measured off a printed dot run — not a CSS border. Kalpurush's period
 * advances 0.229 em, so the run needs a little tracking on top.
 */
export const LEADER = {
    char: '.',
    /** Enough dots to overflow the longest line; each run is clipped. */
    count: 220,
    letterSpacing: 3.05 - TYPE.body.size * 0.229,
};

/**
 * The typed answers are not part of the printed form, so they do not have to
 * match its face: they print larger and bolder than the labels to stay legible
 * on a photocopy. A value that would overrun its blank shrinks down toward
 * `minSize` instead of being clipped.
 */
export const VALUE_TYPE = { size: 12, minSize: 8, weight: 400 };

/** The same, for the digits that print one per comb cell. */
export const CELL_VALUE_TYPE = { size: 12, minSize: 8.5, weight: 400 };

/**
 * Typed values sit above the dots, the way handwriting does — far enough that a
 * Latin descender (g, j, p, q, y) clears the leader instead of sitting on it.
 */
export const VALUE_LIFT = 5;

/** …and start a few points in from the label, not hard against it. */
export const VALUE_INSET = 5;

/** Where a comb cell's digit sits inside its box. */
export const CELL_BASELINE_RATIO = 0.72;

/**
 * …and where a word the form prints *inside* a box sits — the blood groups of
 * ২৩, the স্বামী / স্ত্রী of ১১-১২. Those are optically centred rather than
 * dropped onto the digits' baseline: with Kalpurush's cap at about 0.72 em, the
 * middle of the box puts the baseline a little higher than a digit's.
 */
export const BOX_LABEL_BASELINE_RATIO = 0.66;

/** The Election Commission mark, top-left of page 1. */
export const EMBLEM = { left: 62, top: 57, width: 34, height: 39 };

/**
 * How much room each label actually has before the next thing on its line.
 *
 * Kalpurush sets some of these words wider than the face the form was printed
 * in. The form is the fixed thing — its rules, combs and dot runs were measured
 * off the sheet and must stay where they are — so it is the *label* that gives
 * way: anything that would not fit is set down a little until it does. That
 * keeps every box the size the government printed it.
 */
function withLimits(rows, combs) {
    return rows.map((row) => {
        if (!row.key || row.x == null) return row;
        const band = row.y;
        const rights = [];
        if (row.from != null) rights.push(row.from);
        for (const comb of combs) {
            if (band > comb.top && band < comb.top + comb.height + 6 && comb.left > row.x) {
                rights.push(comb.left);
            }
        }
        for (const other of rows) {
            if (other === row || other.y !== row.y || other.x == null) continue;
            if (other.x > row.x) rights.push(other.x);
        }
        const limit = rights.length ? Math.min(...rights) : PAGE_WIDTH - 12;
        return { ...row, limit: +(limit - row.x - 2).toFixed(1) };
    });
}

/**
 * Every printed line: its baseline, the label that starts it, and — where the
 * form leaves a dotted blank — the span of that blank and the field it takes.
 *
 * The two pages sit on different left margins (70 pt and 44 pt): that is the
 * scan's own registration, not a design difference, and it is kept because the
 * printed sheet is what has to line up.
 */
const PAGE1_ROW_DATA = [
    { y: 70, key: 'formNoLabel', x: 260 },
    { y: 92, key: 'ruleRef', x: 293, size: 'title' },

    { y: 116, key: 'voterAreaNoLabel', x: 369 },
    { y: 133, key: 'voterAreaNameLabel', x: 70, from: 176, to: 470, value: 'voterAreaName' },
    { y: 153, key: 'nameBnLabel', x: 70, from: 152, to: 570, value: 'nameBn' },
    { y: 173, key: 'nameEnLabel', x: 70, from: 226, to: 570, value: 'nameEn' },
    { y: 195, key: 'fatherNameLabel', x: 70, from: 143, to: 570, value: 'fatherName' },

    { y: 217, key: 'fatherNidLabel', x: 70 },
    { y: 217, key: 'notApplicableLabel', x: 513 },
    { y: 245, key: 'orVoterNoLabel', x: 70 },
    { y: 245, key: 'fatherDeathYearLabel', x: 394.5 },

    { y: 272, key: 'motherNameLabel', x: 70, from: 145, to: 570, value: 'motherName' },
    { y: 295, key: 'motherNidLabel', x: 70 },
    { y: 295, key: 'notApplicableLabel', x: 513 },
    { y: 322, key: 'orVoterNoLabel', x: 70 },
    { y: 322, key: 'motherDeathYearLabel', x: 390 },

    { y: 350, key: 'item11', x: 72 },
    { y: 350, key: 'spouseNameLabel', x: 171, from: 208, to: 500, value: 'spouseName' },
    { y: 350, key: 'notApplicableLabel', x: 504 },
    { y: 378, key: 'item12', x: 72 },
    { y: 378, key: 'nidOnlyLabel', x: 164 },
    { y: 378, key: 'notApplicableLabel', x: 504 },
    { y: 405, key: 'item13', x: 72 },
    { y: 405, key: 'husbandLabel', x: 127 },
    { y: 405, key: 'spouseDeathYearLabel', x: 184 },
    { y: 405, key: 'notApplicableLabel', x: 504 },

    { y: 432, key: 'birthRegLabel', x: 72 },
    { y: 455, key: 'dayLabel', x: 169, size: 'small' },
    { y: 455, key: 'monthLabel', x: 208, size: 'small' },
    { y: 455, key: 'yearLabel', x: 270, size: 'small' },
    { y: 471, key: 'birthDateLabel', x: 74 },
    { y: 471, key: 'birthPlaceLabel', x: 329.5, from: 428, to: 575, value: 'birthPlace' },

    { y: 499, key: 'genderLabel', x: 74 },
    { y: 499, key: 'maleLabel', x: 145 },
    { y: 499, key: 'femaleLabel', x: 196 },
    { y: 499, key: 'maritalLabel', x: 242 },
    { y: 499, key: 'unmarriedLabel', x: 334, size: 'small' },
    { y: 499, key: 'marriedLabel', x: 393, size: 'small' },
    { y: 499, key: 'divorcedLabel', x: 447, size: 'small' },
    { y: 499, key: 'widowLabel', x: 506, size: 'small' },
    { y: 499, key: 'widowerLabel', x: 552, size: 'small' },

    { y: 527, key: 'educationLabel', x: 74, from: 303, to: 575, value: 'education' },
    { y: 547, key: 'occupationLabel', x: 74, from: 200, to: 335, value: 'occupation' },
    { y: 547, key: 'disabilityHeadLabel', x: 344.5 },
    { y: 571, key: 'blindLabel', x: 108, size: 'small' },
    { y: 571, key: 'physicalLabel', x: 190, size: 'small' },
    { y: 571, key: 'deafLabel', x: 290, size: 'small' },
    { y: 571, key: 'muteLabel', x: 393, size: 'small' },
    { y: 571, key: 'otherLabel', x: 480, size: 'small', from: 515, to: 570, value: 'disabilityOther' },
    { y: 590, key: 'markLabel', x: 74, from: 200, to: 575, value: 'identificationMark' },
    { y: 612, key: 'bloodLabel', x: 74 },
    { y: 638, key: 'deathNote', x: 72 },

    { y: 690, key: 'slipTitle', center: 325 },
    { y: 704, key: 'slipFormNo', center: 326 },
    { y: 714, key: 'slipRuleRef', center: 326, size: 'small' },
    { y: 732, key: 'slipAreaNoLabel', x: 74 },
    { y: 732, key: 'slipFormNoLabel', x: 357 },
    { y: 750, key: 'slipNameLabel', x: 74, from: 120, to: 575, value: 'nameBn' },
    { y: 768, key: 'slipCentreLabel', x: 74, from: 155, to: 330 },
    { y: 768, key: 'slipAttendLabel', x: 330, from: 458, to: 575 },
    { y: 788, key: 'slipSignLabel', x: 74, from: 232, to: 575 },
];

/** Page 2 — items ২৪ to ৪৬, then the প্রাপ্তি রশিদ. */
const PAGE2_ROW_DATA = [
    { y: 37, key: 'currentAddressLabel', x: 44, from: 175, to: 258, value: 'curDivision' },
    { y: 37, key: 'districtLabel', x: 258, from: 292, to: 365, value: 'curDistrict' },
    { y: 37, key: 'upazilaLabel', x: 365, from: 448, to: 545, value: 'curUpazila' },
    { y: 58, key: 'localBodyLabel', x: 44, from: 235, to: 410, value: 'curLocalBody' },
    { y: 58, key: 'wardLabel', x: 412, from: 470, to: 545, value: 'curWard' },
    { y: 79, key: 'mouzaLabel', x: 44, from: 78, to: 218, value: 'curMouza' },
    { y: 79, key: 'villageLabel', x: 220, from: 345, to: 545, value: 'curVillage' },
    { y: 100, key: 'holdingLabel', x: 44, from: 112, to: 222, value: 'curHolding' },
    { y: 100, key: 'postOfficeLabel', x: 222, from: 255, to: 360, value: 'curPostOffice' },
    { y: 100, key: 'postCodeLabel', x: 378 },
    { y: 123, key: 'rmoLabel', x: 44 },
    { y: 123, key: 'ruralLabel', x: 168 },
    { y: 123, key: 'municipalityLabel', x: 238 },
    { y: 123, key: 'cityLabel', x: 322 },
    { y: 123, key: 'otherAreaLabel', x: 392 },
    { y: 123, key: 'cityCorporationLabel', x: 480 },

    { y: 147, key: 'permanentAddressLabel', x: 44, from: 170, to: 253, value: 'permDivision' },
    { y: 147, key: 'districtLabel', x: 253, from: 290, to: 365, value: 'permDistrict' },
    { y: 147, key: 'upazilaLabel', x: 365, from: 448, to: 545, value: 'permUpazila' },
    { y: 168, key: 'localBodyLabel', x: 44, from: 235, to: 410, value: 'permLocalBody' },
    { y: 168, key: 'wardLabel', x: 412, from: 470, to: 545, value: 'permWard' },
    { y: 190, key: 'mouzaLabel', x: 44, from: 78, to: 218, value: 'permMouza' },
    { y: 190, key: 'villageLabel', x: 220, from: 345, to: 545, value: 'permVillage' },
    { y: 211, key: 'holdingLabel', x: 44, from: 112, to: 222, value: 'permHolding' },
    { y: 211, key: 'postOfficeLabel', x: 222, from: 255, to: 360, value: 'permPostOffice' },
    { y: 211, key: 'postCodeLabel', x: 378 },
    { y: 240, key: 'rmoLabel', x: 44 },
    { y: 240, key: 'ruralLabel', x: 168 },
    { y: 240, key: 'municipalityLabel', x: 238 },
    { y: 240, key: 'cityLabel', x: 322 },
    { y: 240, key: 'otherAreaLabel', x: 392 },
    { y: 240, key: 'cityCorporationLabel', x: 480 },

    { y: 264, key: 'tinLabel', x: 44 },
    { y: 288, key: 'drivingLicenseLabel', x: 44 },
    { y: 311, key: 'passportLabel', x: 44 },
    { y: 334, key: 'telephoneLabel', x: 44, from: 185, to: 318, value: 'telephone' },
    { y: 334, key: 'mobileLabel', x: 326, from: 412, to: 545, value: 'mobile' },
    { y: 357, key: 'religionLabel', x: 44 },
    { y: 357, key: 'islamLabel', x: 160.5 },
    { y: 357, key: 'hinduLabel', x: 232 },
    { y: 357, key: 'christianLabel', x: 310 },
    { y: 357, key: 'buddhistLabel', x: 383 },
    { y: 357, key: 'otherLabel', x: 454, from: 500, to: 545, value: 'religionOther' },

    { y: 378, key: 'excludedHeadLabel', x: 44 },
    { y: 400, key: 'excludedReasonLabel', x: 44, from: 132, to: 545, value: 'excludedReason' },
    { y: 420, key: 'certifyHeadLabel', x: 44 },
    { y: 437, key: 'certifyLine1', x: 68, size: 'note' },
    { y: 455, key: 'certifyLine2', x: 44, size: 'note' },
    { y: 455, key: 'applicantSignLabel', x: 385, from: 512, to: 545 },

    { y: 472, key: 'identifierNidLabel', x: 44 },
    { y: 472, key: 'signature35Label', x: 463, from: 520, to: 545 },
    { y: 497, key: 'collectorNidLabel', x: 44 },
    { y: 497, key: 'signature37Label', x: 464, from: 520, to: 545 },
    { y: 521, key: 'supervisorNidLabel', x: 44 },
    { y: 521, key: 'signature39Label', x: 464, from: 520, to: 545 },
    { y: 547, key: 'verifierNameLabel', x: 44, from: 140, to: 545 },
    { y: 569, key: 'verifierNidLabel', x: 44 },
    { y: 569, key: 'signature42Label', x: 464, from: 520, to: 545 },

    { y: 593, key: 'photoLine1', x: 44, size: 'note' },
    { y: 607, key: 'photoLine2', x: 44, size: 'note', from: 292, to: 400 },
    { y: 607, key: 'dateLabel', x: 402, from: 445, to: 545 },
    { y: 630, key: 'dataEntryNidLabel', x: 44 },
    { y: 630, key: 'signature45Label', x: 461, from: 520, to: 545 },
    { y: 648, key: 'registrarLabel', x: 44, from: 290, to: 545 },

    { y: 684, key: 'receiptTitle', center: 294 },
    { y: 705, key: 'receiptOperatorLabel', x: 44, from: 225, to: 545 },
    { y: 727, key: 'keepNote', center: 294 },
];

/** The dotted rules that tear the slip and the receipt off their page. */
export const SEPARATORS = [
    { page: 1, y: 665, from: 72, to: 578 },
    { page: 2, y: 663, from: 44, to: 545 },
];

/**
 * Comb rows. The scan's cell rules are evenly spaced inside each band, so a
 * group is described by its span and its cell count rather than by listing
 * every rule; `tick` marks the single boxes that take a ✓ rather than digits.
 *
 * Some of those boxes are not empty on the original — ১১-১৩ print স্বামী and
 * স্ত্রী inside theirs, ২৩ prints the blood groups inside its eight — so they
 * carry that text (`label` for a translated one, `text` for a Latin one) and
 * are marked by thickening the box rather than by a ✓ that would land on top
 * of the word already in it.
 */
export const PAGE1_COMBS = [
    { top: 62, height: 16, left: 412, right: 580, cells: 1, value: 'formNo' },
    { top: 102.5, height: 15, left: 487, right: 580, cells: 4, value: 'voterAreaNo' },

    { top: 205, height: 20, left: 169, right: 508, cells: 17, value: 'fatherNid' },
    { top: 204.5, height: 19, left: 564, right: 580, cells: 1, value: 'fatherNidNa', tick: true },
    { top: 235, height: 19, left: 145, right: 382, cells: 12, value: 'fatherVoterNo' },
    { top: 234, height: 19, left: 520, right: 579, cells: 4, value: 'fatherDeathYear' },

    { top: 283, height: 20, left: 169, right: 508, cells: 17, value: 'motherNid' },
    { top: 282, height: 19, left: 564, right: 580, cells: 1, value: 'motherNidNa', tick: true },
    { top: 311, height: 19, left: 145, right: 383, cells: 12, value: 'motherVoterNo' },
    { top: 310, height: 19, left: 520, right: 579, cells: 4, value: 'motherDeathYear' },

    // ১১-১৩ each offer the same স্বামী / স্ত্রী pair before their own blank.
    { top: 339, height: 19, left: 98, right: 127, cells: 1, value: 'spouseKindHusband', tick: true, label: 'husbandLabel' },
    { top: 339, height: 19, left: 139, right: 164, cells: 1, value: 'spouseKindWife', tick: true, label: 'wifeLabel' },
    { top: 337.5, height: 19, left: 562, right: 582, cells: 1, value: 'spouseNameNa', tick: true },
    { top: 367, height: 15, left: 97.5, right: 124, cells: 1, value: 'spouseKindHusband', tick: true, label: 'husbandLabel' },
    { top: 367, height: 15, left: 135.75, right: 157.75, cells: 1, value: 'spouseKindWife', tick: true, label: 'wifeLabel' },
    { top: 365.75, height: 15, left: 210, right: 502, cells: 17, value: 'spouseNid' },
    { top: 365.25, height: 15, left: 560, right: 581.5, cells: 1, value: 'spouseNidNa', tick: true },
    { top: 390.5, height: 19, left: 96, right: 121, cells: 1, value: 'spouseKindHusband', tick: true },
    { top: 390.5, height: 19, left: 150, right: 176, cells: 1, value: 'spouseKindWife', tick: true },
    { top: 389, height: 19, left: 280, right: 378, cells: 4, value: 'spouseDeathYear' },
    { top: 389, height: 19, left: 560.5, right: 580, cells: 1, value: 'spouseDeathNa', tick: true },

    { top: 419, height: 19, left: 205, right: 580, cells: 17, value: 'birthRegNo' },
    { top: 460, height: 19, left: 151, right: 320, cells: 8, value: 'birthDate' },

    { top: 488.5, height: 19, left: 121.25, right: 140.5, cells: 1, value: 'genderMale', tick: true },
    { top: 488.5, height: 19, left: 170.25, right: 191.5, cells: 1, value: 'genderFemale', tick: true },
    { top: 487.5, height: 19, left: 309, right: 328, cells: 1, value: 'maritalUnmarried', tick: true },
    { top: 487.5, height: 19, left: 367.75, right: 387.25, cells: 1, value: 'maritalMarried', tick: true },
    { top: 487.5, height: 19, left: 422, right: 441.25, cells: 1, value: 'maritalDivorced', tick: true },
    { top: 487.5, height: 19, left: 483.25, right: 501.25, cells: 1, value: 'maritalWidow', tick: true },
    { top: 487.5, height: 19, left: 527, right: 547.75, cells: 1, value: 'maritalWidower', tick: true },

    { top: 532.75, height: 18, left: 555.75, right: 575.5, cells: 1, value: 'disabilityNa', tick: true },
    { top: 554, height: 18, left: 163.75, right: 183.25, cells: 1, value: 'disabilityBlind', tick: true },
    { top: 554, height: 18, left: 263.25, right: 283, cells: 1, value: 'disabilityPhysical', tick: true },
    { top: 554, height: 18, left: 356.75, right: 376.5, cells: 1, value: 'disabilityDeaf', tick: true },
    { top: 554, height: 18, left: 452.5, right: 472.25, cells: 1, value: 'disabilityMute', tick: true },

    { top: 598, height: 19, left: 274.75, right: 302.25, cells: 1, value: 'bloodAPlus', tick: true, text: 'A+' },
    { top: 598, height: 19, left: 314, right: 337, cells: 1, value: 'bloodAMinus', tick: true, text: 'A-' },
    { top: 598, height: 19, left: 348.5, right: 376.5, cells: 1, value: 'bloodBPlus', tick: true, text: 'B+' },
    { top: 598, height: 19, left: 388, right: 412.5, cells: 1, value: 'bloodBMinus', tick: true, text: 'B-' },
    { top: 598, height: 19, left: 424, right: 460, cells: 1, value: 'bloodABPlus', tick: true, text: 'AB+' },
    { top: 598, height: 19, left: 470, right: 505, cells: 1, value: 'bloodABMinus', tick: true, text: 'AB-' },
    { top: 598, height: 19, left: 518, right: 545.5, cells: 1, value: 'bloodOPlus', tick: true, text: 'O+' },
    { top: 598, height: 19, left: 557, right: 580.75, cells: 1, value: 'bloodOMinus', tick: true, text: 'O-' },

    { top: 717.5, height: 18.75, left: 172.25, right: 272, cells: 4, value: 'voterAreaNo' },
    { top: 716.5, height: 19, left: 410, right: 581, cells: 1, value: 'formNo' },
];

export const PAGE2_COMBS = [
    { top: 91, height: 15, left: 443, right: 551, cells: 4, value: 'curPostCode' },
    { top: 113.5, height: 15, left: 121.25, right: 156, cells: 1, value: 'curRmoRural', tick: true },
    { top: 113.5, height: 15, left: 195.75, right: 230.5, cells: 1, value: 'curRmoMunicipality', tick: true },
    { top: 113.5, height: 15, left: 279.75, right: 315.5, cells: 1, value: 'curRmoCity', tick: true },
    { top: 113.5, height: 15, left: 349, right: 383.5, cells: 1, value: 'curRmoOther', tick: true },
    { top: 113.5, height: 15, left: 443, right: 472.75, cells: 1, value: 'curRmoCityCorporation', tick: true },

    { top: 200, height: 15, left: 443, right: 551, cells: 4, value: 'permPostCode' },
    { top: 229.25, height: 15, left: 121, right: 155.75, cells: 1, value: 'permRmoRural', tick: true },
    { top: 229.25, height: 15, left: 195.5, right: 230.25, cells: 1, value: 'permRmoMunicipality', tick: true },
    { top: 229.25, height: 15, left: 279.5, right: 315.5, cells: 1, value: 'permRmoCity', tick: true },
    { top: 229.25, height: 15, left: 348.75, right: 383.5, cells: 1, value: 'permRmoOther', tick: true },
    { top: 229.25, height: 15, left: 443, right: 472.75, cells: 1, value: 'permRmoCityCorporation', tick: true },

    { top: 253, height: 15, left: 161, right: 497, cells: 14, value: 'tin' },
    { top: 276.5, height: 15, left: 186, right: 551, cells: 14, value: 'drivingLicense' },
    { top: 300, height: 16, left: 186, right: 551, cells: 14, value: 'passport' },

    { top: 344.25, height: 15, left: 122, right: 149, cells: 1, value: 'religionIslam', tick: true },
    { top: 344.25, height: 15, left: 190, right: 224, cells: 1, value: 'religionHindu', tick: true },
    { top: 344.25, height: 15, left: 269, right: 303, cells: 1, value: 'religionChristian', tick: true },
    { top: 344.25, height: 15, left: 340, right: 375, cells: 1, value: 'religionBuddhist', tick: true },
    { top: 344.25, height: 15, left: 416, right: 450, cells: 1, value: 'religionOtherTick', tick: true },

    { top: 460, height: 16, left: 135.5, right: 458, cells: 17, value: 'identifierNid' },
    { top: 486, height: 15, left: 155.25, right: 458, cells: 17, value: 'collectorNid' },
    { top: 511.25, height: 15, left: 155.25, right: 458, cells: 17, value: null },
    { top: 557.5, height: 15, left: 135.25, right: 458, cells: 17, value: null },
    { top: 616.75, height: 15.5, left: 171.75, right: 455, cells: 17, value: null },
];

/** Rows with the width each label has to live inside. */
export const PAGE1_ROWS = withLimits(PAGE1_ROW_DATA, PAGE1_COMBS);
export const PAGE2_ROWS = withLimits(PAGE2_ROW_DATA, PAGE2_COMBS);

/** Where each answer prints, so the preview can follow the field being typed. */
export const FIELD_ANCHORS = buildFieldAnchors([
    { rows: PAGE1_ROWS, combs: PAGE1_COMBS },
    { rows: PAGE2_ROWS, combs: PAGE2_COMBS },
]);
